export interface Variant {
  id: string;
  name: string;
  weight: number | null;
}

export interface VariantConfig {
  isEnabled: boolean;
  splitType: SplitType;
  variants: Variant[];
}

export type SplitType = 'even' | 'custom';

export const MIN_VARIANTS = 2;
export const MAX_VARIANTS = 5;
export const VARIANT_COUNTS = [2, 3, 4, 5] as const;

// Core variant manipulation functions
export const variantUtils = {
  calculateEvenWeights(count: number): number[] {
    const evenWeight = Math.floor(100 / count);
    const remainder = 100 - evenWeight * count;
    return Array.from({ length: count }, (_, i): number =>
      i === 0 ? evenWeight + remainder : evenWeight
    );
  },

  createNewVariant(index: number, weight: number, existingId?: string): Variant {
    return {
      id: existingId ?? crypto.randomUUID(),
      name: `Variant ${index + 1}`,
      weight
    };
  },

  adjustVariantCount(config: VariantConfig, newCount: number): VariantConfig {
    if (newCount < MIN_VARIANTS || newCount > MAX_VARIANTS) {
      return config;
    }

    const weights = this.calculateEvenWeights(newCount);
    let newVariants: Variant[];

    if (newCount > config.variants.length) {
      // Keep existing variants with new weights
      newVariants = config.variants.map((v, i) => ({
        ...v,
        weight: weights[i]
      }));

      // Add new variants
      for (let i = config.variants.length; i < newCount; i++) {
        newVariants.push(this.createNewVariant(i, weights[i]));
      }
    } else {
      // Keep first n variants with new weights
      newVariants = config.variants.slice(0, newCount).map((v, i) => ({
        ...v,
        weight: weights[i]
      }));
    }

    return {
      ...config,
      variants: newVariants
    };
  },

  changeSplitType(config: VariantConfig, newType: SplitType): VariantConfig {
    if (newType === 'even') {
      const weights = this.calculateEvenWeights(config.variants.length);
      return {
        ...config,
        splitType: newType,
        variants: config.variants.map((v, i) => ({
          ...v,
          weight: weights[i]
        }))
      };
    }

    return {
      ...config,
      splitType: newType
    };
  },

  updateVariantWeight(
    config: VariantConfig,
    variantId: string,
    newWeight: number | null
  ): VariantConfig {
    if (config.splitType === 'even') {
      return config;
    }

    return {
      ...config,
      variants: config.variants.map((variant) =>
        variant.id === variantId ? { ...variant, weight: newWeight } : variant
      )
    };
  }
};
